import * as React from 'react';
import { AlertTriangle, Check, Copy, FileText, Search, Upload, X } from 'lucide-react';
import { TriCheckbox, WorkspaceButton, useToast } from '@/components/ui/workspace';
import { WorkspaceField, WorkspaceInput, WorkspaceSelect } from '@/components/ui/workspace/field';
import { useFirmAiBudgetQuery } from '@/lib/api/ai-costs';
import {
  errorMessage,
  errorReason,
  useAddDomain,
  useKbSettings,
  useRegisterSource,
  useSendUploadLink,
  useUploadDocument,
  useWebSearch,
  type KbSearchReply,
  type KbSourceRow,
  type KbVendor,
} from '@/lib/api/vendor-knowledge';
import { cn } from '@/lib/utils/cn';
import { Actions, Chip, Empty, FieldHelp, FormError, InlineForm, Linkish } from '../kb-bits';
import { formatDate, hostPath, money, plural, possessive } from '../kb-logic';
import { cleanHost } from '../catalog-edit';
import { TextArea } from '../review/review-bits';
import type { SourceFormKind } from '../vendor-page-context';

/**
 * The four ways a curator adds a source, each a small form opened in place
 * at the top of Sources (prototype `sourceForm()`; T-0207): a web page,
 * pages found by a web search, a document, and an upload link that asks
 * the vendor for one. The server checks everything again.
 */

function FormHead({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div className="mb-0.5 flex items-center justify-between gap-2">
      <div className="font-semibold text-ink-900">{title}</div>
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-md text-ink-500 hover:bg-ink-100 hover:text-ink-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-amber-600"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** "$12.40 left this month" when the firm budget can be read by this user. */
function useBudgetLeft(): string | null {
  const budget = useFirmAiBudgetQuery();
  return budget.data ? `${money(budget.data.remaining_cents)} left this month` : null;
}

export function SourceForm({
  kind,
  data,
  sources,
  onClose,
  onSwitch,
}: {
  kind: SourceFormKind;
  data: KbVendor;
  sources: KbSourceRow[];
  onClose: () => void;
  onSwitch: (kind: SourceFormKind) => void;
}) {
  switch (kind) {
    case 'web':
      return <WebPageForm data={data} onClose={onClose} />;
    case 'find':
      return <FindPagesForm data={data} onClose={onClose} onSwitch={onSwitch} />;
    case 'upload':
      return <UploadForm data={data} sources={sources} onClose={onClose} />;
    default:
      return <LinkForm data={data} onClose={onClose} />;
  }
}

const activeHosts = (data: KbVendor) =>
  data.domains.filter((d) => d.status === 'active').map((d) => d.host);

function onVendorDomain(data: KbVendor, url: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return data.domains.some(
      (d) =>
        (d.status === 'active' || d.status === 'proposed') &&
        (host === d.host ||
          host === `www.${d.host}` ||
          (d.include_subdomains && host.endsWith(`.${d.host}`))),
    );
  } catch {
    return false;
  }
}

function ProductSelect({
  data,
  value,
  onChange,
  id,
}: {
  data: KbVendor;
  value: string;
  onChange: (v: string) => void;
  id?: string;
}) {
  const products = data.products.filter((p) => p.status === 'active');
  return (
    <WorkspaceSelect id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">All products</option>
      {products.map((p) => (
        <option key={p.id} value={p.id}>
          {p.name}
        </option>
      ))}
    </WorkspaceSelect>
  );
}

function defaultProduct(data: KbVendor): string {
  const products = data.products.filter((p) => p.status === 'active');
  return products.length === 1 ? products[0]!.id : '';
}

// ---------------------------------------------------------------------------
// Add a web page
// ---------------------------------------------------------------------------

function WebPageForm({ data, onClose }: { data: KbVendor; onClose: () => void }) {
  const { toast } = useToast();
  const budget = useBudgetLeft();
  const register = useRegisterSource();
  const addDomain = useAddDomain();
  const [url, setUrl] = React.useState('');
  const [productId, setProductId] = React.useState(defaultProduct(data));
  const [error, setError] = React.useState<string | null>(null);
  const [offDomain, setOffDomain] = React.useState<string | null>(null);
  const [reasoning, setReasoning] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const hosts = activeHosts(data);

  const capture = (outsideReason?: string) => {
    setError(null);
    const typed = url.trim();
    let parsed: URL | null = null;
    try {
      parsed = new URL(typed);
    } catch {
      parsed = null;
    }
    if (!parsed || !/^https?:$/.test(parsed.protocol)) {
      setError('Enter the full address, starting with https://');
      return;
    }
    if (!outsideReason && !onVendorDomain(data, parsed.href)) {
      setOffDomain(parsed.hostname.replace(/^www\./, ''));
      return;
    }
    register.mutate(
      {
        vendor_id: data.vendor.id,
        url: parsed.href,
        ...(productId ? { product_id: productId } : {}),
        ...(outsideReason ? { outside_domain_reason: outsideReason } : {}),
      },
      {
        onSuccess: (out) => {
          toast({
            message: out.run_id
              ? `Capturing ${hostPath(parsed!.href)}. What AI proposes from it appears under To review.`
              : `Added ${hostPath(parsed!.href)}. It is captured once its domain is approved.`,
          });
          onClose();
        },
        onError: (err) => {
          if (errorReason(err) === 'vk.outside_domain_reason_required') {
            setOffDomain(parsed!.hostname.replace(/^www\./, ''));
          } else {
            setError(errorMessage(err));
          }
        },
      },
    );
  };

  const addTheDomain = () => {
    if (!offDomain) return;
    addDomain.mutate(
      { vendorId: data.vendor.id, host: cleanHost(offDomain) },
      {
        onSuccess: () => {
          toast({
            message: `Added ${offDomain} as one of ${possessive(data.vendor.name)} domains.`,
          });
          setOffDomain(null);
          capture('');
        },
        onError: (err) => setError(errorMessage(err)),
      },
    );
  };

  return (
    <InlineForm aria-label="Add a web page" className="mb-2 mt-0">
      <FormHead title="Add a web page" onClose={onClose} />
      <div className="text-meta text-ink-500">
        A page on {hosts.length ? hosts.join(' or ') : 'the vendor’s own domains'} is captured at
        once. Reading it uses the firm AI budget{budget ? `: ${budget}` : ''}.
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <WorkspaceInput
          autoFocus
          className="min-w-[240px] flex-1"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            setOffDomain(null);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') capture();
          }}
          placeholder={`https://${hosts[0] ?? 'example.com'}/…`}
          aria-label="Page address"
          autoComplete="off"
        />
        {data.products.filter((p) => p.status === 'active').length > 1 ? (
          <ProductSelect data={data} value={productId} onChange={setProductId} />
        ) : null}
        <WorkspaceButton
          variant="primary"
          size="md"
          disabled={register.isPending}
          onClick={() => capture()}
        >
          Capture
        </WorkspaceButton>
      </div>
      {error ? <FormError>{error}</FormError> : null}
      {offDomain ? (
        <div className="mt-3 rounded-card bg-posture-at-risk-tint px-3 py-2.5 text-body-sm text-ink-700">
          <div className="flex flex-wrap items-center gap-2.5">
            <AlertTriangle className="h-4 w-4 flex-none text-posture-at-risk" aria-hidden="true" />
            <span className="min-w-0 flex-1">
              {offDomain} isn’t one of {possessive(data.vendor.name)} web domains.
            </span>
            <WorkspaceButton disabled={addDomain.isPending} onClick={addTheDomain}>
              Add the domain
            </WorkspaceButton>
            <WorkspaceButton variant="tertiary" onClick={() => setReasoning(true)}>
              Approve this page only
            </WorkspaceButton>
          </div>
          {reasoning ? (
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <WorkspaceInput
                autoFocus
                className="min-w-[240px] flex-1"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={`Why does this page belong to ${data.vendor.name}?`}
                aria-label="Why this page belongs to the vendor"
                autoComplete="off"
              />
              <WorkspaceButton
                variant="primary"
                disabled={!reason.trim() || register.isPending}
                onClick={() => capture(reason.trim())}
              >
                Capture this page
              </WorkspaceButton>
            </div>
          ) : null}
        </div>
      ) : null}
    </InlineForm>
  );
}

// ---------------------------------------------------------------------------
// Find pages (web search, on request)
// ---------------------------------------------------------------------------

function FindPagesForm({
  data,
  onClose,
  onSwitch,
}: {
  data: KbVendor;
  onClose: () => void;
  onSwitch: (kind: SourceFormKind) => void;
}) {
  const { toast } = useToast();
  const budget = useBudgetLeft();
  const search = useWebSearch();
  const register = useRegisterSource();
  const [words, setWords] = React.useState('');
  const [others, setOthers] = React.useState(false);
  const [reply, setReply] = React.useState<KbSearchReply | null>(null);
  const [picked, setPicked] = React.useState<Set<string>>(new Set());
  const [reason, setReason] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [notConfigured, setNotConfigured] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const run = () => {
    setError(null);
    setNotConfigured(null);
    search.mutate(
      {
        vendor_id: data.vendor.id,
        ...(words.trim() ? { words: words.trim() } : {}),
        ...(others ? { include_other_sites: true } : {}),
      },
      {
        onSuccess: (out) => {
          setReply(out);
          setPicked(new Set());
        },
        onError: (err) => {
          if (errorReason(err) === 'vk.search_not_configured') setNotConfigured(errorMessage(err));
          else setError(errorMessage(err));
        },
      },
    );
  };

  const candidates = reply?.candidates ?? [];
  const chosen = candidates.filter((c) => picked.has(c.url));
  const offChosen = chosen.some((c) => !c.on_vendor_domains);

  const capture = async () => {
    setBusy(true);
    setError(null);
    let done = 0;
    try {
      for (const c of chosen) {
        await register.mutateAsync({
          vendor_id: data.vendor.id,
          url: c.url,
          origin: 'web_search',
          ...(c.on_vendor_domains ? {} : { outside_domain_reason: reason.trim() }),
        });
        done += 1;
      }
      toast({
        message: `Capturing ${plural(done, 'page')}. What AI proposes from them appears under To review.`,
      });
      onClose();
    } catch (err) {
      setError(`${done > 0 ? `${plural(done, 'page')} added. ` : ''}${errorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <InlineForm aria-label="Find pages" className="mb-2 mt-0">
      <FormHead title="Find pages" onClose={onClose} />
      <div className="text-meta text-ink-500">
        A web search for pages about {possessive(data.vendor.name)} products. You tick each page to
        approve it. Nothing is captured without approval, and the results are not kept.
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <WorkspaceInput
          className="min-w-[220px] flex-1"
          value={words}
          onChange={(e) => setWords(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') run();
          }}
          placeholder="Words to add, such as accounts payable"
          aria-label="Search for"
          autoComplete="off"
          maxLength={80}
        />
        <WorkspaceButton size="md" disabled={search.isPending} onClick={run}>
          <Search className="h-3.5 w-3.5" aria-hidden="true" /> Search
        </WorkspaceButton>
      </div>
      <label className="mt-2 flex items-center gap-2 text-meta text-ink-700">
        <TriCheckbox
          state={others ? 'on' : 'off'}
          onChange={() => setOthers((o) => !o)}
          aria-label="Search other sites too"
        />
        Search other sites too, not only {activeHosts(data).join(', ') || 'the vendor’s domains'}
      </label>
      <FieldHelp>
        A search uses the firm AI budget{budget ? `: ${budget}` : ''}.
        {reply ? ` This search used ${money(reply.cost_cents)}.` : ''}
      </FieldHelp>
      {notConfigured ? (
        <div role="status" className="mt-3">
          <Empty small>
            {notConfigured} <Linkish onClick={() => onSwitch('web')}>Add a web page</Linkish>
          </Empty>
        </div>
      ) : null}
      {error ? <FormError>{error}</FormError> : null}
      {reply ? (
        candidates.length === 0 ? (
          <Empty small className="mt-3">
            The search found no new pages. Add a page you know with Add a web page.
          </Empty>
        ) : (
          <>
            <div className="mt-3 overflow-hidden rounded-card border border-ink-100 bg-paper-0">
              {candidates.map((c) => {
                const exists = !!c.already_registered_source_id;
                return (
                  <div
                    key={c.url}
                    className={cn(
                      'flex items-start gap-3 border-t border-ink-100 px-3 py-2.5 first:border-t-0',
                      exists && 'bg-paper-50',
                    )}
                  >
                    <TriCheckbox
                      state={exists ? 'locked' : picked.has(c.url) ? 'on' : 'off'}
                      aria-label={exists ? `${c.title} is already a source` : `Approve ${c.title}`}
                      className="mt-[3px]"
                      onChange={() =>
                        setPicked((prev) => {
                          const next = new Set(prev);
                          if (next.has(c.url)) next.delete(c.url);
                          else next.add(c.url);
                          return next;
                        })
                      }
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={cn('font-medium', exists ? 'text-ink-500' : 'text-ink-900')}
                        >
                          {c.title}
                        </span>
                        {c.on_vendor_domains ? (
                          <Chip tone="healthy">On {hostPath(new URL(c.url).origin)}</Chip>
                        ) : null}
                        {exists ? (
                          <span className="text-meta text-ink-500">Already a source</span>
                        ) : null}
                      </div>
                      <div className="break-all font-mono text-[11px] leading-[18px] text-ink-500">
                        {c.url}
                      </div>
                      <div className="text-meta text-ink-500">{c.snippet}</div>
                      {c.reason ? (
                        <div className="mt-0.5 text-meta text-ink-700">Why: {c.reason}</div>
                      ) : null}
                      {!c.on_vendor_domains && !exists ? (
                        <div className="mt-1 text-meta text-posture-at-risk">
                          Not one of {possessive(data.vendor.name)} domains. Approving captures this
                          page only.
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
            {offChosen ? (
              <div className="mt-2.5">
                <WorkspaceInput
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={`Why do the pages off ${possessive(data.vendor.name)} domains belong to it?`}
                  aria-label="Why the pages off the vendor’s domains belong to it"
                  autoComplete="off"
                />
              </div>
            ) : null}
            <Actions>
              <span className="flex-1 text-meta text-ink-500">
                {plural(chosen.length, 'page')} approved
              </span>
              <WorkspaceButton
                variant="primary"
                disabled={chosen.length === 0 || busy || (offChosen && !reason.trim())}
                onClick={() => void capture()}
              >
                Capture {plural(chosen.length, 'page')}
              </WorkspaceButton>
            </Actions>
          </>
        )
      ) : null}
    </InlineForm>
  );
}

// ---------------------------------------------------------------------------
// Upload a document
// ---------------------------------------------------------------------------

const ACCEPT = '.pdf,.docx,.xlsx,.xls,.txt,.csv';

function UploadForm({
  data,
  sources,
  onClose,
}: {
  data: KbVendor;
  sources: KbSourceRow[];
  onClose: () => void;
}) {
  const { toast } = useToast();
  const upload = useUploadDocument();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [file, setFile] = React.useState<File | null>(null);
  const [title, setTitle] = React.useState('');
  const [providedBy, setProvidedBy] = React.useState('');
  const [productId, setProductId] = React.useState(defaultProduct(data));
  const [confidential, setConfidential] = React.useState(false);
  const [versionOf, setVersionOf] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const documents = sources.filter((s) => s.kind === 'vendor_document' && s.status !== 'retired');

  const choose = (f: File | null | undefined) => {
    if (!f) return;
    setFile(f);
    setError(null);
    if (!title) setTitle(f.name.replace(/\.[a-z0-9]+$/i, ''));
  };

  const submit = () => {
    if (!file) return;
    setError(null);
    upload.mutate(
      {
        file,
        vendor_id: data.vendor.id,
        confidential,
        ...(versionOf
          ? { new_version_of_source_id: versionOf }
          : {
              title: title.trim() || undefined,
              provided_by: providedBy.trim() || undefined,
              product_id: productId || null,
            }),
      },
      {
        onSuccess: () => {
          toast({
            message: `Uploaded ${file.name}. It is read once the malware scan finds it clean; its passages appear under To review.`,
          });
          onClose();
        },
        onError: (err) => setError(errorMessage(err)),
      },
    );
  };

  return (
    <InlineForm aria-label="Upload a document" className="mb-2 mt-0">
      <FormHead title="Upload a document" onClose={onClose} />
      <div className="text-meta text-ink-500">
        A product guide, release notes, a security or hosting paper, or a standard RFP response. AI
        reads it in full and proposes items for you to confirm.
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        aria-label="Choose a file"
        onChange={(e) => choose(e.target.files?.[0])}
      />
      {file ? (
        <div className="mt-3 flex items-center gap-2.5 rounded-card border border-ink-100 bg-paper-50 px-3 py-2.5 text-body-sm">
          <FileText className="h-4 w-4 flex-none text-ink-500" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="font-medium text-ink-900">{file.name}</span>{' '}
            <span className="text-meta text-ink-500">
              {(file.size / 1024 / 1024).toFixed(1)} MB
            </span>
          </span>
          <button
            type="button"
            aria-label="Remove the file"
            onClick={() => setFile(null)}
            className="inline-flex h-[26px] w-[26px] items-center justify-center rounded-md text-ink-500 hover:bg-ink-100 hover:text-ink-900"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <DropBox
          onPick={() => inputRef.current?.click()}
          onDrop={(f) => choose(f)}
          hint="PDF, Word, Excel or text, up to 50 MB"
          label="Drop a file here"
          or="or choose one"
        />
      )}
      {documents.length > 0 ? (
        <WorkspaceField label="Is it a new version of a document you have?" className="mt-3">
          {(id) => (
            <WorkspaceSelect
              id={id}
              value={versionOf}
              onChange={(e) => setVersionOf(e.target.value)}
            >
              <option value="">No, a new document</option>
              {documents.map((d) => (
                <option key={d.id} value={d.id}>
                  A new version of {d.title ?? 'a document'}
                </option>
              ))}
            </WorkspaceSelect>
          )}
        </WorkspaceField>
      ) : null}
      {versionOf ? (
        <FieldHelp>
          A new version keeps its document’s title, product, provider and confidentiality. Only the
          passages that changed are read.
        </FieldHelp>
      ) : (
        <>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <WorkspaceField label="Title">
              {(id) => (
                <WorkspaceInput
                  id={id}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  autoComplete="off"
                />
              )}
            </WorkspaceField>
            <WorkspaceField label="Provided by">
              {(id) => (
                <WorkspaceInput
                  id={id}
                  value={providedBy}
                  onChange={(e) => setProvidedBy(e.target.value)}
                  placeholder="Name, company"
                  autoComplete="off"
                />
              )}
            </WorkspaceField>
            {data.products.filter((p) => p.status === 'active').length > 1 ? (
              <WorkspaceField label="Product">
                {(id) => (
                  <ProductSelect id={id} data={data} value={productId} onChange={setProductId} />
                )}
              </WorkspaceField>
            ) : null}
          </div>
          <ConfidentialCheck checked={confidential} onChange={setConfidential} />
        </>
      )}
      {error ? <FormError>{error}</FormError> : null}
      <Actions>
        <WorkspaceButton variant="primary" disabled={!file || upload.isPending} onClick={submit}>
          {upload.isPending ? 'Uploading…' : 'Upload and read'}
        </WorkspaceButton>
        <WorkspaceButton variant="tertiary" onClick={onClose}>
          Cancel
        </WorkspaceButton>
      </Actions>
    </InlineForm>
  );
}

/** The vendor-confidential choice (Decision 60), as the prototype words it. */
export function ConfidentialCheck({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="mt-3.5 flex items-start gap-2.5 text-body-sm">
      <TriCheckbox
        state={checked ? 'on' : 'off'}
        onChange={() => onChange(!checked)}
        aria-label="Vendor-confidential"
        className="mt-0.5"
      />
      <span>
        <span className="block font-medium text-ink-900">Vendor-confidential</span>
        <span className="block text-meta text-ink-500">
          Its items are used in consultant screens and in maps. Client pages and exports cite it by
          title and date only, without the quote.
        </span>
      </span>
    </div>
  );
}

/** A dashed drop zone that also opens the file picker (`.kb-drop`). */
export function DropBox({
  onPick,
  onDrop,
  label,
  or,
  hint,
  multiple = false,
  onDropMany,
}: {
  onPick: () => void;
  onDrop?: (f: File) => void;
  onDropMany?: (files: File[]) => void;
  label: string;
  or: string;
  hint: string;
  multiple?: boolean;
}) {
  const [over, setOver] = React.useState(false);
  return (
    <button
      type="button"
      onClick={onPick}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const files = Array.from(e.dataTransfer.files);
        if (multiple) onDropMany?.(files);
        else if (files[0]) onDrop?.(files[0]);
      }}
      className={cn(
        'mt-3 flex w-full flex-col items-center gap-1 rounded-large border-[1.5px] border-dashed border-ink-200 bg-paper-0 p-5 text-body-sm text-ink-700',
        'hover:border-ink-300 hover:bg-paper-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-amber-600',
        over && 'border-accent-amber-500 bg-accent-amber-50',
      )}
    >
      <Upload className="h-[18px] w-[18px] text-ink-500" aria-hidden="true" />
      <span>
        <b className="font-semibold">{label}</b> {or}
      </span>
      <span className="text-meta text-ink-500">{hint}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Send an upload link
// ---------------------------------------------------------------------------

function LinkForm({ data, onClose }: { data: KbVendor; onClose: () => void }) {
  const settings = useKbSettings();
  const send = useSendUploadLink();
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [purpose, setPurpose] = React.useState('');
  const [productId, setProductId] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [sent, setSent] = React.useState<{ to: string; url: string | null; until: string } | null>(
    null,
  );
  const [copied, setCopied] = React.useState(false);
  const days = settings.data?.link_ttl_days;
  const ok = /\S+@\S+\.\S+/.test(email.trim()) && purpose.trim().length >= 5;
  const hosts = activeHosts(data);

  const submit = () => {
    setError(null);
    send.mutate(
      {
        vendor_id: data.vendor.id,
        purpose: purpose.trim(),
        recipient_email: email.trim(),
        ...(name.trim() ? { recipient_name: name.trim() } : {}),
        ...(productId ? { product_id: productId } : {}),
      },
      {
        onSuccess: (out) =>
          setSent({
            to: name.trim() || email.trim(),
            url: typeof out.url === 'string' ? out.url : null,
            until: out.expires_at,
          }),
        onError: (err) => setError(errorMessage(err)),
      },
    );
  };

  if (sent) {
    return (
      <InlineForm aria-label="Upload link sent" className="mb-2 mt-0">
        <FormHead title="Upload link sent" onClose={onClose} />
        <div className="flex items-start gap-2 text-body-sm text-ink-900">
          <Check className="mt-0.5 h-4 w-4 flex-none text-posture-healthy" aria-hidden="true" />
          <span>
            Sent to {sent.to}. It works until {formatDate(sent.until)}. Files they send wait under
            To review until you accept them.
          </span>
        </div>
        <Actions>
          {sent.url ? (
            <WorkspaceButton
              onClick={() => {
                void navigator.clipboard?.writeText(sent.url!).then(() => setCopied(true));
              }}
            >
              <Copy className="h-3.5 w-3.5" aria-hidden="true" /> {copied ? 'Copied' : 'Copy link'}
            </WorkspaceButton>
          ) : null}
          <WorkspaceButton variant="tertiary" onClick={onClose}>
            Done
          </WorkspaceButton>
        </Actions>
      </InlineForm>
    );
  }

  return (
    <InlineForm aria-label="Send an upload link" className="mb-2 mt-0">
      <FormHead title="Send an upload link" onClose={onClose} />
      <div className="text-meta text-ink-500">
        The vendor gets a page that shows only your request and a place to drop files. Nothing else
        in Caliber is visible. Files wait here until you accept them.
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <WorkspaceField label="Contact name">
          {(id) => (
            <WorkspaceInput
              id={id}
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
            />
          )}
        </WorkspaceField>
        <WorkspaceField label="Email">
          {(id) => (
            <WorkspaceInput
              id={id}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={`name@${hosts[0] ?? 'vendor.com'}`}
              autoComplete="off"
            />
          )}
        </WorkspaceField>
      </div>
      <WorkspaceField label="What you’d like" className="mt-3">
        {(id) => (
          <TextArea
            id={id}
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="For example: your current security overview"
            maxLength={200}
          />
        )}
      </WorkspaceField>
      {data.products.filter((p) => p.status === 'active').length > 1 ? (
        <WorkspaceField label="About which product (optional)" className="mt-3">
          {(id) => <ProductSelect id={id} data={data} value={productId} onChange={setProductId} />}
        </WorkspaceField>
      ) : null}
      <FieldHelp>
        {days
          ? `The link works for ${days} days, the firm’s secure-link setting. You can revoke it; to give more time, send a new one.`
          : 'You can revoke the link; to give more time, send a new one.'}
      </FieldHelp>
      {error ? <FormError>{error}</FormError> : null}
      <Actions>
        <WorkspaceButton variant="primary" disabled={!ok || send.isPending} onClick={submit}>
          Send link
        </WorkspaceButton>
        <WorkspaceButton variant="tertiary" onClick={onClose}>
          Cancel
        </WorkspaceButton>
      </Actions>
    </InlineForm>
  );
}
